import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface MobileBottomNavProps {
  isLoggedIn: boolean;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Auto-hide after 3 seconds of inactivity
    const resetHideTimer = () => {
      setIsVisible(true);
      
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    };

    // Show on any touch or mouse movement
    const handleInteraction = () => {
      resetHideTimer();
    };

    // Initial timer
    resetHideTimer();

    // Listen for user interactions
    window.addEventListener('touchstart', handleInteraction, { passive: true });
    window.addEventListener('touchmove', handleInteraction, { passive: true });
    window.addEventListener('mousemove', handleInteraction, { passive: true });
    window.addEventListener('click', handleInteraction, { passive: true });

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('touchmove', handleInteraction);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };
  }, []);

  if (!isLoggedIn) return null;

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-100 px-2 py-2 flex items-center justify-around z-40 safe-area-inset-bottom transition-transform duration-300 ${
      isVisible ? 'translate-y-0' : 'translate-y-full'
    }`}>
      <button 
        onClick={() => navigate('/dashboard')}
        className="flex flex-col items-center gap-0.5 px-4 py-2 rounded-full transition-colors"
      >
        <span className={`material-symbols-outlined text-2xl ${isActive('/dashboard') ? 'text-primary fill-icon' : 'text-slate-600'}`}>home</span>
        <span className={`text-[11px] font-medium ${isActive('/dashboard') ? 'text-primary' : 'text-slate-600'}`}>Home</span>
      </button>
      <button 
        onClick={() => navigate('/drive')}
        className="flex flex-col items-center gap-0.5 px-4 py-2 rounded-full transition-colors"
      >
        <span className={`material-symbols-outlined text-2xl ${isActive('/drive') ? 'text-primary fill-icon' : 'text-slate-600'}`}>folder</span>
        <span className={`text-[11px] font-medium ${isActive('/drive') ? 'text-primary' : 'text-slate-600'}`}>My Drive</span>
      </button>
      <button 
        onClick={() => navigate('/profile')}
        className="flex flex-col items-center gap-0.5 px-4 py-2 rounded-full transition-colors"
      >
        <span className={`material-symbols-outlined text-2xl ${isActive('/profile') ? 'text-primary fill-icon' : 'text-slate-600'}`}>person</span>
        <span className={`text-[11px] font-medium ${isActive('/profile') ? 'text-primary' : 'text-slate-600'}`}>Profile</span>
      </button>
      <button 
        onClick={() => navigate('/billing')}
        className="flex flex-col items-center gap-0.5 px-4 py-2 rounded-full transition-colors"
      >
        <span className={`material-symbols-outlined text-2xl ${isActive('/billing') ? 'text-primary fill-icon' : 'text-slate-600'}`}>credit_card</span>
        <span className={`text-[11px] font-medium ${isActive('/billing') ? 'text-primary' : 'text-slate-600'}`}>Billing</span>
      </button>
    </nav>
  );
};
